<template>
	<div class="loading-overlay-container">
		<!-- Opaque background -->
		<div class="loading-overlay-modal-background" />

		<!-- Inner body -->
		<div class="loading-inner-container">
			<h2 style="text-align: center">Contract Version Conflict</h2>
			<div class="conflict-message">
				<p>
					Changes have been made to this contract since you started editing. Please review the
					conflicts below:
				</p>
			</div>

			<div class="conflicts-container">
				<div
					v-for="(conflict, index) in conflicts"
					:key="index"
					class="conflict-item"
				>
					<div class="conflict-field">
						<span class="field-name">{{ conflict.field }}</span>
						<div class="conflict-values">
							<div class="value-container">
								<label>Your changes:</label>
								<div class="value local">{{ conflict.localValue }}</div>
							</div>
							<div class="value-container">
								<label>Server changes:</label>
								<div class="value server">{{ conflict.serverValue }}</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="resolution-options">
				<h3>Resolution Options:</h3>
				<div class="option-buttons">
					<button
						class="resolution-btn keep-mine"
						:class="{ selected: selectedResolution === 'keep-mine' }"
						@click="resolveConflict('keep-mine')"
					>
						Keep My Changes
					</button>
					<button
						class="resolution-btn take-server"
						:class="{ selected: selectedResolution === 'take-server' }"
						@click="resolveConflict('take-server')"
					>
						Take Server Changes
					</button>
				</div>
			</div>

			<div class="alert-buttons-container">
				<button
					class="cancel-btn"
					@click="cancel()"
				>
					Cancel
				</button>
				<button
					class="submit-btn"
					:disabled="!selectedResolution"
					@click="submit()"
				>
					Continue
				</button>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'ContractConflictModal',
		props: {
			conflicts: {
				type: Array,
				required: true,
				default: () => []
			}
		},
		data() {
			return {
				selectedResolution: null
			}
		},
		methods: {
			/**
			 * Resolve the conflict
			 * @param {string} resolution - The resolution to keep
			 */
			resolveConflict(resolution) {
				this.selectedResolution = resolution
			},
			/**
			 * Cancel the conflict
			 */
			cancel() {
				this.$emit('cancel')
			},
			/**
			 * Submit the conflict
			 */
			submit() {
				if (!this.selectedResolution) {
					return
				}
				this.$emit('resolve', {
					resolution: this.selectedResolution,
					conflicts: this.conflicts
				})
			}
		}
	}
</script>

<style scoped>
	.loading-overlay-container {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		z-index: 9999;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.loading-overlay-modal-background {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		z-index: 9998;
	}

	.loading-inner-container {
		position: relative;
		background: #ffffff;
		padding: 2em;
		border-radius: 8px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		z-index: 9999;
		max-width: 600px;
		width: 90%;
		max-height: 90vh;
		overflow-y: auto;
		box-sizing: border-box;
	}

	.conflict-message {
		text-align: center;
		margin-bottom: 20px;
		padding: 0 20px;
	}

	.conflict-message p {
		color: #333333;
		font-size: 1em;
		line-height: 1.5;
		margin: 0;
	}

	.conflicts-container {
		max-height: 300px;
		overflow-y: auto;
		margin-bottom: 20px;
		padding: 0;
		box-sizing: border-box;
	}

	.conflict-item {
		margin-bottom: 20px;
		padding: 15px;
		border: 1px solid rgba(51, 51, 51, 0.2);
		border-radius: 8px;
		background: #fff;
	}

	.conflict-field {
		margin-bottom: 10px;
	}

	.field-name {
		font-weight: 500;
		color: #333333;
		font-size: 1.1em;
		margin-bottom: 10px;
		display: block;
	}

	.conflict-values {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.value-container {
		flex: 1;
		padding: 10px;
		background: rgba(0, 0, 0, 0.02);
		border-radius: 4px;
	}

	.value-container label {
		display: block;
		font-size: 0.9em;
		color: #666;
		margin-bottom: 5px;
	}

	.value {
		font-family: monospace;
		padding: 8px;
		background: white;
		border-radius: 4px;
		border: 1px solid rgba(51, 51, 51, 0.1);
		word-break: break-word;
	}

	.resolution-options {
		padding: 0;
		margin-bottom: 20px;
	}

	.resolution-options h3 {
		font-size: 1.1em;
		color: #333333;
		margin-bottom: 15px;
	}

	.option-buttons {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.resolution-btn {
		width: 100%;
		padding: 12px;
		border-radius: 8px;
		border: 2px solid rgba(51, 51, 51, 0.2);
		background: white;
		cursor: pointer;
		transition: all 0.2s ease;
		font-size: 14px;
		font-weight: 500;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.resolution-btn::before {
		content: '';
		position: absolute;
		left: 12px;
		width: 18px;
		height: 18px;
		border: 2px solid rgba(51, 51, 51, 0.3);
		border-radius: 50%;
		transition: all 0.2s ease;
	}

	.resolution-btn.selected {
		background: rgba(0, 122, 255, 0.05);
	}

	.resolution-btn.selected::before {
		background: #007aff;
		border-color: #007aff;
	}

	.resolution-btn:hover {
		background: rgba(0, 122, 255, 0.05);
	}

	.resolution-btn.keep-mine {
		color: #007aff;
		border-color: #007aff;
	}

	.resolution-btn.keep-mine.selected::before {
		background: #007aff;
		border-color: #007aff;
	}

	.resolution-btn.take-server {
		color: #333333;
	}

	.resolution-btn.take-server.selected::before {
		background: #333333;
		border-color: #333333;
	}

	.alert-buttons-container {
		margin-top: 32px;
		display: flex;
		justify-content: space-between;
		gap: 16px;
		width: 100%;
		box-sizing: border-box;
	}

	.cancel-btn,
	.submit-btn {
		flex: 1;
		min-height: 40px;
		padding: 8px 24px;
		font-size: 14px;
		font-weight: 500;
		border-radius: 8px;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.cancel-btn {
		background-color: #ffffff;
		color: #333333;
		border: 1px solid rgba(51, 51, 51, 0.7);
	}

	.cancel-btn:hover {
		background-color: #f5f5f5;
	}

	.submit-btn {
		background-color: #007aff;
		color: #ffffff;
		border: none;
	}

	.submit-btn:hover {
		background-color: #0066d6;
	}

	.submit-btn:disabled {
		background-color: #cccccc;
		cursor: not-allowed;
	}

	/* Add base button styles */
	button {
		font-family: inherit;
		margin: 0;
		overflow: visible;
		text-transform: none;
		appearance: button;
		-webkit-appearance: button;
		cursor: pointer;
	}

	button::-moz-focus-inner {
		border-style: none;
		padding: 0;
	}

	/* Add box-sizing to all elements */
	* {
		box-sizing: border-box;
	}
</style>
